import CryptoJS from 'crypto-js'
import { useCallback, useEffect, useState } from 'react'

/**
 * NOTE: For AES‑CBC you need a 16‑byte (128‑bit) key & IV.
 * Here we derive both by UTF‑8‑parsing your SECRET_KEY (truncate/pad as needed).
 * In production, pick a strong random IV and store it (e.g. prefix to the hex).
 */
const SECRET_KEY = 'LEGAL_TECHNOLOGY_H' // must be exactly 16 chars
const KEY = CryptoJS.enc.Utf8.parse(SECRET_KEY)
const IV = CryptoJS.enc.Utf8.parse(SECRET_KEY)

/** Generate a highly‑unique ID: timestamp + ms + random */
const generateUniqueId = (): string => {
  const now = Date.now()
  const ts = now.toString()
  const ms = String(now % 1000).padStart(3, '0')
  const rand = Math.random().toString(36).slice(2, 8)
  const proc = (Math.random() * 1000).toString(36).slice(2, 6)
  return `${ts}${ms}${rand}${proc}`
}

/** AES‑CBC encrypt → hex */
const encodeUniqueId = (id: string): string => {
  const encrypted = CryptoJS.AES.encrypt(id, KEY, {
    iv: IV,
    mode: CryptoJS.mode.CBC,
    padding: CryptoJS.pad.Pkcs7,
  })
  // ciphertext is a WordArray; hex stringify it and uppercase A–F
  return encrypted.ciphertext.toString(CryptoJS.enc.Hex).toUpperCase()
}

/** AES‑CBC decrypt from hex */
const decodeUniqueId = (hexCipher: string): string | null => {
  try {
    const cipherParams = CryptoJS.lib.CipherParams.create({
      ciphertext: CryptoJS.enc.Hex.parse(hexCipher),
    })
    const decrypted = CryptoJS.AES.decrypt(cipherParams, KEY, {
      iv: IV,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7,
    })
    const txt = decrypted.toString(CryptoJS.enc.Utf8)
    return txt || null
  } catch {
    // eslint-disable-next-line no-console
    console.error('Failed to decrypt ID')
    return null
  }
}

/** Standalone function to get encoded unique ID for use in custom wrapper */
export const getEncodedUniqueId = (): string => {
  const uniqueId = generateUniqueId()
  return encodeUniqueId(uniqueId)
}

/** One‐stop hook: get raw ID, encrypted hex ID, and a re‑generator */
export const useUniqueId = () => {
  const [rawId, setRawId] = useState<string>('')
  const [hexId, setHexId] = useState<string>('')

  const generateNew = useCallback(() => {
    const TempUniqueID = generateUniqueId()
    setRawId(TempUniqueID)
    setHexId(encodeUniqueId(TempUniqueID))
  }, [])

  useEffect(() => {
    generateNew()
  }, [generateNew])

  return {
    rawId, // the plain unique ID
    encryptedHexId: hexId, // AES‑CBC hex (0–9, A–F only)
    decode: decodeUniqueId,
    regenerate: generateNew,
  }
}
